// src/pages/GradesPage/GradesPage.jsx
import React, { useState, useEffect } from "react";
import { PageContainer } from "../../components/layout";
import { Loading } from "../../components/common";
import { VIEW_MODES } from "./constants";
import { filterBySearchTerm, getSemesterMonths, getMaxPoints } from "./utils";
import { useGradesData, useGradeSelection, useAssessmentModal } from "./hooks";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faBookOpen,
	faUsers,
	faCheck,
} from "@fortawesome/free-solid-svg-icons";

import {
	GradesHeader,
	SubjectsView,
	StudentsView,
	AssessmentGradingModal,
	MobileGradesView,
} from "./components";
import styles from "./GradesPage.module.css";

const GradesPage = () => {
	// Mobile detection state
	const [isMobile, setIsMobile] = useState(false);

	// Mobile detection effect
	useEffect(() => {
		const checkMobile = () => {
			setIsMobile(window.innerWidth <= 768);
		};

		checkMobile();
		window.addEventListener("resize", checkMobile);

		return () => window.removeEventListener("resize", checkMobile);
	}, []);

	const {
		classData,
		isLoading,
		classError,
		departmentSubjects,
		students,
		departmentId,
	} = useGradesData();

	const {
		viewMode,
		selectedSubject,
		selectedStudent,
		selectedSemester,
		grades,
		searchTerm,
		isLoadingGrades,
		gradesError,
		setSelectedSemester,
		setSearchTerm,
		handleViewModeChange,
		handleSubjectSelect,
		handleStudentSelect,
	} = useGradeSelection(departmentId);

	const {
		showAssessmentModal,
		selectedGradeData,
		monthAssessments,
		studentGrades,
		isLoading: isLoadingModal,
		isSaving,
		handleGradeCellClick,
		closeAssessmentModal,
		handleSaveGrades,
	} = useAssessmentModal();

	// Mobile-specific calculation functions
	const calculateStudentTotal = (studentId) => {
		if (!selectedSubject) return 0;
		const studentKey = `${studentId}_${selectedSubject.id}`;
		const studentData = grades[studentKey];

		if (!studentData?.months) return 0;

		let total = 0;
		const semesterMonths = getSemesterMonths(selectedSemester);

		semesterMonths.forEach((month) => {
			const monthGrade = studentData.months[month.id];
			if (monthGrade) {
				const maxPoints = getMaxPoints(classData.gradeLevel, month.id);
				total += (monthGrade.total / maxPoints) * 25; // Each month is 25% of total
			}
		});

		return Math.round(total);
	};

	const calculateSubjectTotal = (subjectKey, semester) => {
		const subjectData = grades[subjectKey];

		if (!subjectData?.months) return 0;

		let total = 0;
		const semesterMonths = getSemesterMonths(semester);

		semesterMonths.forEach((month) => {
			const monthGrade = subjectData.months[month.id];
			if (monthGrade) {
				const maxPoints = getMaxPoints(classData.gradeLevel, month.id);
				total += (monthGrade.total / maxPoints) * 25;
			}
		});

		return Math.round(total);
	};

	if (isLoading) {
		return (
			<PageContainer className={styles.gradesPage}>
				<Loading />
			</PageContainer>
		);
	}

	if (classError) {
		return (
			<PageContainer className={styles.gradesPage}>
				<div className={styles.errorState}>
					<h2>Error loading class data</h2>
					<p>Unable to load class information. Please try again.</p>
				</div>
			</PageContainer>
		);
	}

	if (!classData) {
		return (
			<PageContainer className={styles.gradesPage}>
				<div className={styles.emptyState}>
					<h2>No class data found</h2>
					<p>No class information found for this department.</p>
				</div>
			</PageContainer>
		);
	}

	const filteredStudents = filterBySearchTerm(students, searchTerm, [
		"name",
		"studentId",
	]);

	return (
		<PageContainer className={styles.gradesPage}>
			{!isMobile ? (
				// Desktop View
				<>
					<GradesHeader
						classData={classData}
						viewMode={viewMode}
						selectedSemester={selectedSemester}
						onViewModeChange={handleViewModeChange}
						onSemesterChange={setSelectedSemester}
					/>

					{/* Main layout */}
					<div className={styles.layout}>
						{/* Sidebar section */}
						<div className={styles.sidebar}>
							<div className={styles.section}>
								<h3 className={styles.sectionTitle}>
									<FontAwesomeIcon
										icon={
											viewMode === VIEW_MODES.SUBJECTS ? faBookOpen : faUsers
										}
									/>
									{viewMode === VIEW_MODES.SUBJECTS
										? "All Subjects"
										: "Students"}
								</h3>

								{viewMode === VIEW_MODES.SUBJECTS ? (
									<div className={styles.subjectList}>
										{departmentSubjects.length > 0 ? (
											departmentSubjects.map((subject) => (
												<button
													key={subject.id}
													className={`${styles.subjectItem} ${
														subject.teacherAssigned ? styles.assigned : ""
													} ${
														selectedSubject?.id === subject.id
															? styles.selected
															: ""
													}`}
													onClick={() => handleSubjectSelect(subject)}
												>
													<div className={styles.subjectHeader}>
														<span className={styles.subjectName}>
															{subject.name}
														</span>
														{subject.teacherAssigned && (
															<FontAwesomeIcon
																icon={faCheck}
																className={styles.assignedIcon}
															/>
														)}
													</div>
													<span className={styles.subjectClass}>
														{subject.class}
													</span>
												</button>
											))
										) : (
											<div className={styles.emptySubjects}>
												<p>No subjects found for this class.</p>
											</div>
										)}
									</div>
								) : (
									<div className={styles.studentList}>
										{students.length > 0 ? (
											students.map((student) => (
												<button
													key={student.id}
													className={`${styles.studentItem} ${
														selectedStudent?.id === student.id
															? styles.selected
															: ""
													}`}
													onClick={() => handleStudentSelect(student)}
												>
													<div className={styles.studentHeader}>
														<span className={styles.studentName}>
															{student.name}
														</span>
													</div>
													<span className={styles.studentId}>
														{student.studentId}
													</span>
												</button>
											))
										) : (
											<div className={styles.emptyStudents}>
												<p>No students found for this class.</p>
											</div>
										)}
									</div>
								)}
							</div>
						</div>

						<div className={styles.content}>
							{viewMode === VIEW_MODES.SUBJECTS ? (
								<SubjectsView
									selectedSubject={selectedSubject}
									selectedSemester={selectedSemester}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									students={filteredStudents}
									grades={grades}
									classData={classData}
									departmentId={departmentId}
									isLoadingGrades={isLoadingGrades}
									gradesError={gradesError}
									onGradeCellClick={handleGradeCellClick}
								/>
							) : (
								<StudentsView
									selectedStudent={selectedStudent}
									selectedSemester={selectedSemester}
									searchTerm={searchTerm}
									setSearchTerm={setSearchTerm}
									grades={grades}
									classData={classData}
									isLoadingGrades={isLoadingGrades}
									gradesError={gradesError}
									departmentSubjects={departmentSubjects}
								/>
							)}
						</div>
					</div>
				</>
			) : (
				// Mobile View
				<div className={styles.mobileLayout}>
					<div className={styles.mobileContent}>
						<MobileGradesView
							viewMode={viewMode}
							selectedSubject={selectedSubject}
							selectedStudent={selectedStudent}
							selectedSemester={selectedSemester}
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							students={students}
							departmentSubjects={departmentSubjects}
							grades={grades}
							classData={classData}
							isLoadingGrades={isLoadingGrades}
							gradesError={gradesError}
							onSubjectSelect={handleSubjectSelect}
							onStudentSelect={handleStudentSelect}
							onGradeCellClick={handleGradeCellClick}
							onSemesterChange={setSelectedSemester}
							onViewModeChange={handleViewModeChange} // Add this prop
							calculateTotal={
								viewMode === VIEW_MODES.SUBJECTS
									? calculateStudentTotal
									: calculateSubjectTotal
							}
						/>
					</div>
				</div>
			)}

			{/* Assessment Grading Modal */}
			<AssessmentGradingModal
				isOpen={showAssessmentModal}
				onClose={closeAssessmentModal}
				studentId={selectedGradeData?.studentId}
				studentName={selectedGradeData?.studentName}
				subjectId={selectedGradeData?.subjectId}
				subjectName={selectedGradeData?.subjectName}
				month={selectedGradeData?.monthName}
				departmentId={selectedGradeData?.departmentId}
				assessments={monthAssessments}
				studentGrades={studentGrades}
				onSaveGrades={handleSaveGrades}
				isLoading={isLoadingModal}
				isSaving={isSaving}
			/>
		</PageContainer>
	);
};

export default GradesPage;
